import { getActiveBlankReasons } from 'lib/telemetry/blankReasons';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { getForegroundPerfNow } from 'lib/telemetry/foregroundTime';
import { getFailureCounts } from 'lib/telemetry/recentFailures';

/**
 * Detects the "navbar renders, content area stays empty" state directly, since
 * the paths that cause it mostly render `null` without throwing anything RUM
 * could see.
 *
 * `EdgeToEdgeLayout` and `CleanLayout` — the only layouts that render the
 * navbar — mark their content area with `data-content-slot`. After 5s and 15s
 * of foreground time on each view, the watchdog checks whether those slots
 * hold any text or media. A blank check emits a `blank_content` RUM action
 * (at most one per stage per view) carrying:
 *
 * - `pending_reasons`: who registered as deliberately rendering nothing
 *   (`lib/telemetry/blankReasons`) — Suspense fallbacks, gates, boundaries.
 * - `pending_ssr_boundaries` / `client_rendered_boundaries`: React's streaming
 *   markers still in the slot. A pending one means the server stream never
 *   delivered that segment; a client-rendered one means the server render
 *   errored and React fell back to rendering it in the browser.
 * - navigation shape, hydration timing, failure counts, connectivity.
 *
 * If the view later gets content, `blank_content_recovered` records how long it
 * took. If it doesn't, `blank_content_unrecovered` fires once when the user
 * leaves the blank view (`exit`: `navigated` to another route, tab `hidden`, or
 * `poll_limit` when the watchdog stops polling), so stuck pages are a count
 * rather than "blank reports minus recoveries". The recovery poll runs once a
 * second, so a view can gain content up to a second before a `navigated` exit
 * and still be reported unrecovered. A `hidden` view that later recovers
 * reports both. The first detection also force-starts session replay (sampled
 * at 1% otherwise).
 *
 * Triage in the RUM explorer with
 *   service:trading-portal env:prod @type:action @action.name:blank_content @context.stage:late
 * broken down by @context.pending_reasons / @view.name / @context.navigation_kind.
 */

type CheckStage = 'early' | 'late';

type UnrecoveredExit = 'navigated' | 'hidden' | 'poll_limit';

type NavigationKind =
    | 'hard_navigate'
    | 'hard_reload'
    | 'hard_back_forward'
    | 'hard_prerender'
    | 'client_push'
    | 'client_back_forward'
    | 'bfcache_restore';

interface WatchedView {
    pathname: string;
    previousPathname: string | null;
    navigationKind: NavigationKind;
    /** Only compared by identity, and dropped with the view, so a detached page isn't retained. */
    slot: Element | null;
    /** Whether the navbar layout instance changed, i.e. the navigation crossed route groups. */
    layoutRemounted: boolean | null;
    startForegroundMs: number;
    checkTimer: ReturnType<typeof setTimeout> | null;
    recoveryTimer: ReturnType<typeof setTimeout> | null;
    firstBlank: { stage: CheckStage; foregroundMs: number } | null;
    outcome: 'recovered' | 'unrecovered' | null;
}

export const CONTENT_SLOT_ATTRIBUTE = 'data-content-slot';
const CONTENT_SLOT_SELECTOR = `[${CONTENT_SLOT_ATTRIBUTE}]`;
// Anything a user would read as "the page has something on it" without text.
const MEDIA_SELECTOR = 'img, svg, canvas, video, iframe, picture, input, select, textarea';
// `SHIMMER_CLASSES` (animate-skeleton-shimmer) matches `skeleton`; the rest are one-off shapes.
const SKELETON_SELECTOR = '[class*="animate-pulse"], [class*="skeleton" i], [aria-busy="true"]';

const CHECKS: ReadonlyArray<{ stage: CheckStage; foregroundMs: number }> = [
    { stage: 'early', foregroundMs: 5_000 },
    { stage: 'late', foregroundMs: 15_000 },
];
const RECOVERY_POLL_MS = 1_000;
const MAX_RECOVERY_POLLS = 120;
// A pathname change this soon after `popstate` came from back/forward.
const POPSTATE_WINDOW_MS = 1_000;

let view: WatchedView | null = null;
let hydratedAtPerfNow: number | null = null;
let lastPopstatePerfNow = -Infinity;
let replayForced = false;
let contextProvider: (() => Record<string, unknown>) | null = null;

/**
 * Lets a layer that already holds app state (auth) enrich the report without
 * pulling those stores into this module's graph, which loads on every route.
 */
export function setBlankContentContextProvider(provider: (() => Record<string, unknown>) | null): void {
    contextProvider = provider;
}

/**
 * Starts watching the view for `pathname`. Called from `DatadogRouteTracker`'s
 * layout effect, so the first call is the hydration commit and later calls are
 * client navigations, each after the new route has committed.
 */
export function armBlankContentWatchdog(pathname: string): void {
    if (typeof window === 'undefined' || view?.pathname === pathname) return;

    const now = performance.now();
    if (hydratedAtPerfNow === null) {
        hydratedAtPerfNow = now;
        window.addEventListener('popstate', handlePopstate);
        window.addEventListener('pageshow', handlePageShow);
        // Window capture, registered at hydration, runs ahead of the RUM SDK's own
        // exit listener (also window capture, added at the later deferred init), so
        // the report joins the batch the SDK flushes as the page goes away.
        window.addEventListener('visibilitychange', handleVisibilityChange, { capture: true });
        // The initial view started at navigation start (the performance time origin), not at hydration.
        startView(pathname, getInitialNavigationKind(), 0);
        return;
    }

    const kind = now - lastPopstatePerfNow <= POPSTATE_WINDOW_MS ? 'client_back_forward' : 'client_push';
    startView(pathname, kind, getForegroundPerfNow(now));
}

/** Test-only. */
export function stopBlankContentWatchdog(): void {
    if (view) clearTimers(view);
    view = null;
    hydratedAtPerfNow = null;
    lastPopstatePerfNow = -Infinity;
    replayForced = false;
    if (typeof window !== 'undefined') {
        window.removeEventListener('popstate', handlePopstate);
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('visibilitychange', handleVisibilityChange, { capture: true });
    }
}

function handlePopstate(): void {
    lastPopstatePerfNow = performance.now();
}

function handlePageShow(event: PageTransitionEvent): void {
    // A bfcache restore shows the page as it was, so blank-after-restore is its own view.
    if (event.persisted && view) startView(view.pathname, 'bfcache_restore', getForegroundPerfNow());
}

function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden' && view) reportUnrecovered(view, 'hidden');
}

function getInitialNavigationKind(): NavigationKind {
    const entry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    switch (entry?.type) {
        case 'reload':
            return 'hard_reload';
        case 'back_forward':
            return 'hard_back_forward';
        case 'prerender':
            return 'hard_prerender';
        default:
            return 'hard_navigate';
    }
}

function startView(pathname: string, navigationKind: NavigationKind, startForegroundMs: number): void {
    const previous = view;
    if (previous) {
        clearTimers(previous);
        reportUnrecovered(previous, 'navigated');
    }

    const slot = document.querySelector(CONTENT_SLOT_SELECTOR);
    view = {
        pathname,
        previousPathname: previous?.pathname ?? null,
        navigationKind,
        slot,
        layoutRemounted: previous?.slot && slot ? slot !== previous.slot : null,
        startForegroundMs,
        checkTimer: null,
        recoveryTimer: null,
        firstBlank: null,
        outcome: null,
    };
    scheduleCheck(view, 0);
}

function clearTimers(watched: WatchedView): void {
    if (watched.checkTimer !== null) clearTimeout(watched.checkTimer);
    if (watched.recoveryTimer !== null) clearTimeout(watched.recoveryTimer);
    watched.checkTimer = null;
    watched.recoveryTimer = null;
}

function scheduleCheck(watched: WatchedView, index: number): void {
    const check = CHECKS[index];
    if (!check) return;

    // Hidden time doesn't count: a background tab isn't a stuck page. The foreground
    // clock stands still while hidden, so an early wake-up just waits for the remainder.
    const deadline = watched.startForegroundMs + check.foregroundMs;
    const arm = (): void => {
        watched.checkTimer = setTimeout(wait, Math.max(0, deadline - getForegroundPerfNow()));
    };
    const wait = (): void => {
        watched.checkTimer = null;
        if (view !== watched) return;
        if (deadline > getForegroundPerfNow()) {
            arm();
            return;
        }
        runCheck(watched, check.stage);
        scheduleCheck(watched, index + 1);
    };
    arm();
}

function findSlots(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>(CONTENT_SLOT_SELECTOR));
}

function slotHasContent(slot: HTMLElement): boolean {
    // Cheap first: nearly every rendered page has an icon or image.
    if (slot.querySelector(MEDIA_SELECTOR)) return true;
    // Then stop at the first visible text, rather than `innerText`, which forces layout
    // and builds the whole page's text on every check.
    const walker = document.createTreeWalker(slot, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const parent = node.parentElement;
        if (node.textContent?.trim() && (!parent?.checkVisibility || parent.checkVisibility())) return true;
    }
    return false;
}

const anySlotHasContent = (): boolean => findSlots().some(slotHasContent);

function countSuspenseMarkers(slot: HTMLElement): { pending: number; clientRendered: number } {
    // React's streaming SSR markers: `<!--$?-->` is a boundary still waiting for its
    // streamed segment, `<!--$!-->` one the server gave up on and left to the client.
    let pending = 0;
    let clientRendered = 0;
    const walker = document.createTreeWalker(slot, NodeFilter.SHOW_COMMENT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const data = (node as Comment).data;
        if (data === '$?') pending++;
        else if (data === '$!') clientRendered++;
    }
    return { pending, clientRendered };
}

/** Report fields describing the slots, or null when the page has content or isn't a navbar layout. */
function inspectBlankSlots(): Record<string, number | boolean> | null {
    const slots = findSlots();
    if (slots.length === 0 || slots.some(slotHasContent)) return null;

    const snapshot = {
        slot_count: slots.length,
        element_count: 0,
        has_skeleton: false,
        pending_ssr_boundaries: 0,
        client_rendered_boundaries: 0,
    };
    for (const slot of slots) {
        const markers = countSuspenseMarkers(slot);
        snapshot.element_count += slot.getElementsByTagName('*').length;
        snapshot.has_skeleton ||= slot.querySelector(SKELETON_SELECTOR) !== null;
        snapshot.pending_ssr_boundaries += markers.pending;
        snapshot.client_rendered_boundaries += markers.clientRendered;
    }
    return snapshot;
}

function runCheck(watched: WatchedView, stage: CheckStage): void {
    const snapshot = inspectBlankSlots();
    if (!snapshot) return;

    report('blank_content', { stage, ...describe(watched), ...snapshot });

    if (!watched.firstBlank) {
        watched.firstBlank = { stage, foregroundMs: getForegroundPerfNow() };
        scheduleRecoveryPoll(watched, 0);
    }
    if (!replayForced) {
        replayForced = true;
        // Replay records from here on: whether the page recovers, and what the user does.
        datadogRum.startSessionReplayRecording({ force: true });
    }
}

function scheduleRecoveryPoll(watched: WatchedView, polls: number): void {
    watched.recoveryTimer = setTimeout(() => {
        watched.recoveryTimer = null;
        if (view !== watched || !watched.firstBlank) return;
        if (anySlotHasContent()) {
            watched.outcome = 'recovered';
            const now = getForegroundPerfNow();
            report('blank_content_recovered', {
                pathname: watched.pathname,
                navigation_kind: watched.navigationKind,
                first_blank_stage: watched.firstBlank.stage,
                foreground_ms_until_content: Math.round(now - watched.startForegroundMs),
                foreground_ms_blank_after_report: Math.round(now - watched.firstBlank.foregroundMs),
            });
            return;
        }
        if (polls + 1 < MAX_RECOVERY_POLLS) scheduleRecoveryPoll(watched, polls + 1);
        else reportUnrecovered(watched, 'poll_limit');
    }, RECOVERY_POLL_MS);
}

/** At most once per view, and only for one that was reported blank and hasn't recovered. */
function reportUnrecovered(watched: WatchedView, exit: UnrecoveredExit): void {
    if (!watched.firstBlank || watched.outcome !== null) return;
    watched.outcome = 'unrecovered';
    const now = getForegroundPerfNow();
    report('blank_content_unrecovered', {
        pathname: watched.pathname,
        navigation_kind: watched.navigationKind,
        first_blank_stage: watched.firstBlank.stage,
        exit,
        foreground_ms_since_navigation: Math.round(now - watched.startForegroundMs),
        foreground_ms_blank_after_report: Math.round(now - watched.firstBlank.foregroundMs),
    });
}

function describe(watched: WatchedView): Record<string, unknown> {
    const now = performance.now();
    const failures = getFailureCounts(now);
    let providedContext: Record<string, unknown> = {};
    try {
        providedContext = contextProvider?.() ?? {};
    } catch (error) {
        console.warn('[BlankContent] context provider threw; report will lack app state', error);
    }

    return {
        pathname: watched.pathname,
        previous_pathname: watched.previousPathname,
        navigation_kind: watched.navigationKind,
        layout_remounted: watched.layoutRemounted,
        foreground_ms_since_navigation: Math.round(getForegroundPerfNow(now) - watched.startForegroundMs),
        ms_since_hydration: hydratedAtPerfNow === null ? null : Math.round(now - hydratedAtPerfNow),
        hydrated_at_ms: hydratedAtPerfNow === null ? null : Math.round(hydratedAtPerfNow),
        pending_reasons: getActiveBlankReasons(),
        online: navigator.onLine,
        visibility: document.visibilityState,
        recent_failures: failures.recent,
        page_failures: failures.total,
        ...providedContext,
    };
}

function report(name: string, context: Record<string, unknown>): void {
    // An action rather than an error: this shouldn't page anyone until we know the base rate.
    datadogRum.addAction(name, context);
    if (process.env.NODE_ENV === 'development') {
        // RUM only runs in production; this is how the watchdog is checked locally.
        console.info(`[BlankContent] ${name}`, context);
    }
}
